import {
    championshipPrestigeXps,
    fanLevels,
    generateName,
    getArrayMaxByProperty,
    getChunks, getHairColor,
    getRandomChoice,
    getRandomColor,
    getRandomInt,
    getRandomSample,
    getRosterMemberPronoun,
    myround,
    objectInArray,
    staffFirstNames, staffLastNames,
    staffMartianFirstNames,
    staffMartianLastNames,
    weightedChoice
} from "./helpers.js";
import {getEventHype} from "./events.js";
import {getRankFromPopularity} from "./superstars.js";
import {
    addCompanyChampionships,
    addCompanyEvents,
    addCompanyFeudItems, addCompanyGimmickMatches, addCompanyPowerups,
    addCompanyRosters, addCompanySegmentItems,
    Company,
    getCompanyEvents
} from "../models/company.js";
import {addChildEvent, Event} from "../models/event.js";
import {
    ChampionshipReigns,
    Championships,
    Companies,
    Events,
    FeudItems,
    GimmickMatches, Gimmicks,
    Powerups,
    Rosters,
    SegmentItems, StaffMembers, Superstars, TagTeams
} from "../models/models.js";
import {
    addRosterFactions,
    addRosterSuperstars,
    addRosterTagTeams,
    Roster
} from "../models/roster.js";
import {FeudItem} from "../models/feudItem.js";
import {SegmentItem} from "../models/segmentItem.js";
import {Powerup} from "../models/powerup.js";
import {GimmickMatch} from "../models/gimmickMatch.js";
import {
    addChampionshipChampionshipReigns,
    Championship
} from "../models/championship.js";
import {
    addSuperstarChampionshipReigns,
    addSuperstarChampionships,
    addSuperstarGimmicks,
    Superstar
} from "../models/superstar.js";
import {ChampionshipReign} from "../models/championshipReign.js";
import {addTagTeamSuperstars, getTagTeamSuperstars, TagTeam} from "../models/tagTeam.js";
import {companyNames} from "../data/generate.js";
import {createFaction, getFactionCreationReputationAndXp} from "./factions.js";
import {getPartTimerAppearances} from "./rosters.js";
import {formatCompanyNameBackend} from "../ui/ui-helpers.js";
import {addGameCompanies} from "../models/game.js";
import {standardGimmickMatchesData} from "../data/gimmickMatches.js";
import {standardPowerupsData} from "../data/powerups.js";
import {standardFeudItemsData} from "../data/feudItems.js";
import {standardSegmentItemsData} from "../data/segmentItems.js";
import {
    championshipsGraphicsData, graphicsData, jacketsColorsList, shirtsColorsList, glassesColorsList, shoesColorsList,
    pantsColorsList, hairColorsList, bodyColorsList, doctorCoatColorsList
} from "../data/graphics.js";
import {Staff} from "../models/staff.js";
import {globalUser} from "./globals.js";
import {Gimmick} from "../models/gimmick.js";
import {traitsData} from "../data/traits.js";

async function generateCompany({startingEventIndex = null, existingCompanyNames = null, superstars = null, tagTeams = null,
                               ownerName = null, month = 0, year = 0, realYear = null, game = null, uncommonNames = false,
                               mainRostersOption = null}) {  // generate_company
    let ranks = {'Main Eventer': 4, 'Upper Midcarder': 3, 'Lower Midcarder': 2, 'Jabroni': 1};

    // Create company
    let company = Company();
    await Companies.insert(company);
    await addGameCompanies(game, [company]);

    // Add name
    let companyName = generateCompanyName(uncommonNames);
    // Check for duplicate company names
    if (existingCompanyNames) {
        while (existingCompanyNames.includes(companyName)) {
            companyName = generateCompanyName(uncommonNames);
        }
    }
    company.name = companyName;

    // Add owner
    if (ownerName) {
        company.ownerName = ownerName;
    }

    // Add style
    if (getRandomInt(0, 100) <= 40) {
        company.style = 'entertainment';
    } else {
        company.style = 'pure';
    }

    // Add real year
    company.realYear = realYear;

    // Generate events
    let existingEventNames = [];
    for (let y of Array(12).keys()) {
        let newEvent = await generateEvent(existingEventNames);
        await addCompanyEvents(company, [newEvent]);
        existingEventNames.push(newEvent.name);
    }

    // Add starting event with prestige
    let events = await getCompanyEvents(company);
    let topEvent = getArrayMaxByProperty(events, 'xp')
    let parentEvent = events[startingEventIndex];
    let prestige = parentEvent.prestige;
    let startingEventIsTop = parentEvent.$loki === topEvent.$loki;
    let hype = await getEventHype(parentEvent, startingEventIsTop);
    let startingEvent = Event(events[startingEventIndex].name);
    await Events.insert(startingEvent);
    startingEvent.prestige = prestige;
    startingEvent.hype = hype;
    await addCompanyEvents(company, [startingEvent]);
    await addChildEvent(parentEvent, startingEvent);

    // Set top event xp
    let topXps = {'A': 160, 'B': 80, 'C': 25};
    topEvent.xp = topXps[topEvent.prestige] || 0;

    // Add main roster
    let mainRoster = Roster('main');
    await Rosters.insert(mainRoster);
    await addCompanyRosters(company, [mainRoster]);

    // Add superstars
    let rosterSuperstars = [];
    if (superstars) {
        // Keep existing
        rosterSuperstars = superstars;
    } else {
        // Generate new
        for (let y of Array(getRandomInt(20, 90)).keys()) {  // Between 2 and 8 rep
            let newSuperstar = await generateSuperstar({indie: true});
            rosterSuperstars.push(newSuperstar);
        }
    }
    await addRosterSuperstars(mainRoster, rosterSuperstars);

    // Add tag teams
    let rosterTagTeams = [];
    if (tagTeams) {
        rosterTagTeams = tagTeams;
    }
    else {
        let superstars = getRandomSample([...rosterSuperstars], getRandomInt(4, 10));
        let superstarsData = getChunks(superstars, 2);
        for (let z of superstarsData) {
            let newTagteam = await generateTagTeam(z);
            rosterTagTeams.push(newTagteam);
        }
    }
    await addRosterTagTeams(mainRoster, rosterTagTeams);

    // Chance to add trios
    if (getRandomInt(0, 100) <= 40) {
        let superstars = getRandomSample([...rosterSuperstars], getRandomInt(6, 15));
        let superstarsData = getChunks(superstars, 3);
        for (let z of superstarsData) {
            let newTrio = await generateTrio(z);
            rosterTagTeams.push(newTrio);
            await addRosterTagTeams(mainRoster, [newTrio]);
        }
    }

    // Factions
    let rosterFactions = [];
    let superstarsAvailableForFactions = [...rosterSuperstars];
    if (globalUser.paid) {
        for (let y of Array(getRandomInt(2, 5)).keys()) {
            if (superstarsAvailableForFactions.length > 8) {
                let factionSuperstars = getRandomSample(superstarsAvailableForFactions, getRandomInt(2, 8));
                superstarsAvailableForFactions = superstarsAvailableForFactions
                    .filter(x => !objectInArray(x, factionSuperstars));
                let newFaction = await generateFaction(factionSuperstars, rosterFactions, rosterTagTeams);
                await addRosterFactions(mainRoster, [newFaction]);
                rosterFactions.push(newFaction);
            }
        }
    }

    // Add training roster
    let trainingRoster = Roster('training');
    await Rosters.insert(trainingRoster);
    await addCompanyRosters(company, [trainingRoster]);

    // Add training superstars
    let companyTrainingSuperstars = [];
    if (game.settings['generate_indie_superstars'] !== false) {
        for (let x of Array(getRandomInt(10, 14)).keys()) {
            let newSuperstar = await generateSuperstar();
            companyTrainingSuperstars.push(newSuperstar);
        }
    }
    await addRosterSuperstars(trainingRoster, companyTrainingSuperstars);

    if (companyTrainingSuperstars.length) {
        // Add training tag teams
        let superstars = getRandomSample([...companyTrainingSuperstars], getRandomInt(2, 4));
        let superstarsData = getChunks(superstars, 2);
        for (let z of superstarsData) {
            let newTagteam = await generateTagTeam(z);
            await addRosterTagTeams(trainingRoster, [newTagteam]);
        }
        // Chance to add training trios
        if (getRandomInt(0, 100) <= 40) {
            let superstars = getRandomSample([...companyTrainingSuperstars], getRandomInt(6, 9));
            let superstarsData = getChunks(superstars, 3);
            for (let z of superstarsData) {
                let newTrio = await generateTrio(z);
                await addRosterTagTeams(trainingRoster, [newTrio]);
            }
        }
    }

    // Add training available roster
    let trainingAvailableRoster = Roster('trainingAvailable');
    await Rosters.insert(trainingAvailableRoster);
    await addCompanyRosters(company, [trainingAvailableRoster]);
    let trainingFutureRoster = Roster('trainingFuture');
    await Rosters.insert(trainingFutureRoster);
    await addCompanyRosters(company, [trainingFutureRoster]);

    // Add championships
    let femaleSuperstars = rosterSuperstars.filter(x => x.gender === 'female');
    let championshipNames = [];
    for (let c of Array(getRandomInt(1, 2)).keys()) {
        let prestiges = ['C', 'B'];

        // Select gender
        let womens = false;
        let championshipGender = 'male';
        if (femaleSuperstars.length > 4 && getRandomInt(0, 100) <= 30) {
            championshipGender = 'female';
            womens = true;
        }

        // Generate name, don't allow duplicates
        let rankName;
        let championshipName;
        if (c === 0) {
            rankName = 'Main Eventer';
            prestiges.push('A');
        } else {
            rankName = getRandomChoice(['Main Eventer', 'Upper Midcarder', 'Lower Midcarder', 'Jabroni']);
        }
        championshipName = generateChampionshipName(company.name, rankName, null, womens);
        while (championshipNames.includes(championshipName)) {
            championshipName = generateChampionshipName(company.name, rankName, null, womens);
        }
        championshipNames.push(championshipName);

        let newChampionship = Championship(championshipName.trim());
        await Championships.insert(newChampionship);
        newChampionship.graphic = generateChampionshipGraphic();
        newChampionship.gender = championshipGender;
        newChampionship.rank = rankName;
        await addCompanyChampionships(company, [newChampionship]);

        if (getRandomInt(0, 100) <= 70 && mainRostersOption !== 'draft') {
            // Add champion of same or greater rank of championship
            let eligible = rosterSuperstars
                .filter(x => (ranks[getRankFromPopularity(x.popularity)] || 1) >= ranks[rankName] &&
                    x.gender === newChampionship.gender);
            if (eligible.length) {
                let champion = getRandomChoice(eligible);
                await addSuperstarChampionships(champion, [newChampionship]);

                // Add reign
                let newReign = ChampionshipReign(month, year);
                await ChampionshipReigns.insert(newReign);
                await addChampionshipChampionshipReigns(newChampionship, [newReign]);
                await addSuperstarChampionshipReigns(champion, [newReign]);
            } else {
                newChampionship.vacated = true;
            }
        } else {
            newChampionship.vacated = true;
        }

        // Add prestige
        newChampionship.prestige = getRandomChoice(prestiges);
        newChampionship.highestPrestige = newChampionship.prestige;
        newChampionship.xp = championshipPrestigeXps[newChampionship.prestige] || 0;
        // Add xp over prestige base value
        if (newChampionship.prestige !== 'C') {
            newChampionship.xp += 10;
        }
    }

    // Add tag team championships
    let rankName = getRandomChoice(['Lower Midcarder', 'Jabroni']);
    let championshipName = generateChampionshipName(company.name, rankName, 'tag-team');
    await addNewTagChampionshipsToCompany(championshipName, rankName, company, rosterTagTeams.filter(x => !x.trio),
        'tag-team', month, year);

    // Add trio championships
    let rosterTrios = rosterTagTeams.filter(x => x.trio);
    if (rosterTrios.length) {
        rankName = getRandomChoice(['Lower Midcarder', 'Jabroni']);
        championshipName = generateChampionshipName(company.name, rankName, 'trio');
        await addNewTagChampionshipsToCompany(championshipName, rankName, company, rosterTrios, 'trio', month, year);
    }

    // Add standard gimmick matches
    for (let x of standardGimmickMatchesData) {
        let newGimmickMatch = {...GimmickMatch(), ...JSON.parse(JSON.stringify(x))};
        await GimmickMatches.insert(newGimmickMatch);
        await addCompanyGimmickMatches(company, [newGimmickMatch]);
    }

    // Add reputation and fans
    let rosterSize = rosterSuperstars.length;
    // Adjusting for formula and negative values
    rosterSize -= 15;
    if (rosterSize <= 0) {
        rosterSize = 1;
    }
    let reputation = Math.round(rosterSize ** 0.5);
    company.reputation = reputation;
    company.highestReputation = reputation;
    company.fans = fanLevels[reputation - 1];
    company.points = myround(getRandomInt(500, 1200), 50);
    company.overness = 45 + getRandomInt(-3, 5);

    // Add standard segment and feud items, powerups
    for (let x of standardSegmentItemsData) {
        let newSegmentItem = {...SegmentItem(), ...JSON.parse(JSON.stringify(x))};
        await SegmentItems.insert(newSegmentItem);
        await addCompanySegmentItems(company, [newSegmentItem]);
    }
    for (let x of standardFeudItemsData) {
        let newFeudItem = {...FeudItem(), ...JSON.parse(JSON.stringify(x))};
        await FeudItems.insert(newFeudItem);
        await addCompanyFeudItems(company, [newFeudItem]);
    }
    if (company.storyName !== 'mars') {
        const standardPowerups = filterCompetitorPowerups(standardPowerupsData);
        for (const x of standardPowerups) {
            let newPowerup = {...Powerup(), ...JSON.parse(JSON.stringify(x))};
            await Powerups.insert(newPowerup);
            await addCompanyPowerups(company, [newPowerup]);
        }
    }

    return company
}

async function generateCustomStartingCompany({}) {
    let data = {};

    // Generate company attributes
    let companyName = generateCompanyName();
    let companyStyle = (getRandomInt(0, 100) <= 60) ? 'entertainment' : 'pure';
    let ownerGender = (getRandomInt(0, 100) <= 60) ? 'male' : 'female';
    let ownerName = generateName(ownerGender);
    let weeklyShowName = generateWeeklyShowName();
    let companySize = getRandomChoice(['backyard', 'indie', 'national', 'global']);
    let companyFinances = getRandomChoice(['weak', 'average', 'strong', 'loaded']);

    // Generate events
    let eventsData = [];
    let prestigesData = [['C', 10], ['B', 8], ['A', 2]];
    let eventNames = [];
    for (let y = 0; y < 12; y++) {
        let eventName = generateEventName(eventNames);
        eventNames.push(eventName);
        eventsData.push({'name': eventName, 'prestige': weightedChoice(prestigesData)});
    }

    // Generate championships
    let championshipsData = [];
    let championshipName = generateChampionshipName(companyName, 'Main Eventer');
    championshipsData.push({
        'name': championshipName,
        'prestige': getRandomChoice(['A', 'B']),
        'division': 'singles'
    });
    if (getRandomInt(0, 100) <= 70) {
        championshipName = generateChampionshipName(companyName, 'Main Eventer', 'tag-team');
        championshipsData.push({
            'name': championshipName,
            'prestige': getRandomChoice(['A', 'B', 'C']),
            'division': 'tag-team'
        });
    }
    if (getRandomInt(0, 100) <= 70) {
        championshipName = generateChampionshipName(companyName, 'Main Eventer', null, true);
        championshipsData.push({
            'name': championshipName,
            'prestige': getRandomChoice(['A', 'B', 'C']),
            'division': 'singles'
        });
    }
    if (getRandomInt(0, 100) <= 70) {
        championshipName = generateChampionshipName(companyName, '');
        championshipsData.push({
            'name': championshipName,
            'prestige': getRandomChoice(['B', 'C']),
            'division': 'singles'
        });
    }

    // Get data
    data['companyName'] = companyName;
    data['companySize'] = companySize;
    data['companyFinances'] = companyFinances;
    data['companyStyle'] = companyStyle;
    data['ownerName'] = ownerName;
    data['weeklyShowName'] = weeklyShowName;
    data['companyEventsData'] = eventsData;
    data['companyChampionshipsData'] = championshipsData;

    return data;
}

function generateWeeklyShowName() {
    let daysOfTheWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    let weeklyShowNames = ['Conflict', 'Showdown', 'Combat', 'War', 'Idols', 'Explosion', 'Outbreak',
                           'BANG', 'Eruption', 'Storm', 'Surge', 'Uprising', 'Flash', 'Fury', 'Rage',
                           'Destruction', 'Turmoil', 'Revolution', 'Attitude', 'Recoil', 'Gauntlet', 'Mythics',
                           'Power', 'Mayhem', 'Anarchy', 'Insanity', 'Limelight', 'Gore', 'Slugfest', 'Disaster',
                           'Pride', 'Visions', 'Apocalypse', 'Nightmare', 'Horrors'];
    if (getRandomInt(0, 100) <= 20) {
        return `${getRandomChoice(daysOfTheWeek)} Night ${getRandomChoice(weeklyShowNames)}`;
    }
    return getRandomChoice(weeklyShowNames);
}

function filterCompetitorPowerups(powerups) {  // filter_competitor_powerups
    return powerups.filter(x => !['reroll_goal', 'one_match', 'staff_recruitment',
        'gimmick_match_roulette'].includes(x.effect));
}

async function addNewTagChampionshipsToCompany(name, rankName, company, divisionMembers, teamType = 'tag-team',
                                               month = 0, year = 0) {  // add_new_tag_championships_to_company
    let divisionsChampionshipsData = {'tag-team': 2, 'trio': 3};
    let divisionsChampionships = divisionsChampionshipsData[teamType];

    let newChampionships = Array.from({length: divisionsChampionships}, () => Championship(name));
    let graphic = generateChampionshipGraphic();
    for (let newChampionship of newChampionships) {
        await Championships.insert(newChampionship);
        if (teamType === 'trio') {
            newChampionship.trio = true;
        } else {
            newChampionship.tagTeam = true;
        }
        newChampionship.rank = rankName;
        newChampionship.graphic = graphic;
        await addCompanyChampionships(company, [newChampionship]);
    }

    if (getRandomInt(0, 100) <= 70) {
        if (divisionMembers.length) {
            let championTagTeam = getRandomChoice(divisionMembers);
            let championTagTeamSuperstars = await getTagTeamSuperstars(championTagTeam);
            for (let i = 0; i < newChampionships.length; i++) {
                await addSuperstarChampionships(championTagTeamSuperstars[i], [newChampionships[i]]);

                // Add reign
                let newReign = ChampionshipReign(month, year);
                await ChampionshipReigns.insert(newReign);
                await addChampionshipChampionshipReigns(newChampionships[i], [newReign]);
                await addSuperstarChampionshipReigns(championTagTeamSuperstars[i], [newReign]);
            }
        } else {
            for (let newChampionship of newChampionships) {
                newChampionship.vacated = true;
            }
        }
    } else {
        for (let newChampionship of newChampionships) {
            newChampionship.vacated = true;
        }
    }

    return newChampionships;
}

function generateChampionshipName(companyName, rankName, teamType = null, isWomen = false) {  // generate_championship_name
    const championshipPrefix = formatCompanyNameBackend(companyName);
    let firstSuffixes = [];
    let suffixes;

    if (rankName === 'Main Eventer') {
        suffixes = ['World', 'Global', 'National', '', 'Heavyweight', 'Undisputed', 'World Heavyweight'];
    } else {
        suffixes = ['Intercontinental', 'Regional', 'Hardcore', 'Legends', 'Television', 'International',
            'Rookie', 'TV', 'European', 'Super', 'Internet', 'United States',
            'All Action', 'Ironman', 'Shoot', 'North American', 'People\'s',
            'United Kingdom', 'Primetime', 'Pure', 'Platinum', 'Silver'];
    }

    if (teamType) {
        if (teamType === 'tag-team') {
            firstSuffixes = ['Tag', 'Tag Team'];
        } else if (teamType === 'trio') {
            firstSuffixes = ['Trios', '6 Person', 'Six-Person'];
        }
    }

    if (isWomen) {
        firstSuffixes = ['Women', 'Women\'s', 'Ladies'];
    }

    let championshipName;
    if (teamType || isWomen) {
        if (getRandomInt(0, 100) <= 40) {
            championshipName = `${championshipPrefix} ${getRandomChoice(firstSuffixes)} ${getRandomChoice(suffixes)}`;
        } else {
            championshipName = `${championshipPrefix} ${getRandomChoice(firstSuffixes)}`;
        }
    } else {
        championshipName = `${championshipPrefix} ${getRandomChoice(suffixes)}`;
    }

    return championshipName;
}

function generateChampionshipGraphic() {
    let data = {};
    let centerPlateChoices = championshipsGraphicsData['centerPlate'];
    let sidePlatesChoices = championshipsGraphicsData['sidePlates'];

    // Select center plate
    let centerPlateObject = getRandomChoice(centerPlateChoices);
    let centerPlateObjectNumber = centerPlateObject['number'];
    let centerPlateColor = [getChampionshipGraphicPlateColor()];
    data['centerPlate'] = {
        'object': centerPlateObjectNumber,
        'colors': centerPlateColor
    };

    // Set strap
    let strapObjectNumber = centerPlateObject['strapPart'];
    let strapColor = [getChampionshipGraphicStrapColor()];
    data['strap'] = {
        'object': strapObjectNumber,
        'colors': strapColor
    };

    // Set center gems
    let centerGemsObjectNumber = centerPlateObject['gemPart'];
    let centerGemsColor = [getChampionshipGraphicGemsColor()];
    data['centerGems'] = {
        'object': centerGemsObjectNumber,
        'colors': centerGemsColor
    };

    // Set side plates
    let sidePlatesObject;
    let sidePlatesObjectNumber;
    if (getRandomInt(0, 100) <= 70) {
        sidePlatesObjectNumber = centerPlateObject['sidePlatesPart'];
        sidePlatesObject = sidePlatesChoices[sidePlatesObjectNumber];
    } else {
        sidePlatesObject = getRandomChoice(sidePlatesChoices);
        sidePlatesObjectNumber = sidePlatesObject['number'];
    }
    let sidePlatesColor = getRandomInt(0, 100) <= 95 ?
        [centerPlateColor] : [getChampionshipGraphicPlateColor()];
    data['sidePlates'] = {
        'object': sidePlatesObjectNumber,
        'colors': sidePlatesColor
    };

    // Set side gems
    let sideGemsObjectNumber = sidePlatesObject['sideGemsPart'];
    let sideGemsColor = getRandomInt(0, 100) <= 95 ?
        [centerGemsColor] : [getChampionshipGraphicGemsColor()];
    data['sideGems'] = {
        'object': sideGemsObjectNumber,
        'colors': sideGemsColor
    };

    return data;
}

function getChampionshipGraphicStrapColor(excludeMainColors = false, secondaryColorsChance = 95) {  // generate_championship_graphic_strap_color
    const mainColors = ["0x1D1D1D"];
    const secondaryColors = ["0x981E21", "0x2F4F7D", "0xE5E5E5", "0x2F6232", "0x83264F", "0x3B2363"];
    if (getRandomInt(0, 100) <= 70 && !excludeMainColors) {
        return getRandomChoice(mainColors);
    } else {
        if (getRandomInt(0, 100) <= secondaryColorsChance) {
            return getRandomChoice(secondaryColors);
        } else {
            return getRandomColor();
        }
    }
}

function getChampionshipGraphicPlateColor(excludeMainColors = false, secondaryColorsChance = 95) {  // get_championship_graphic_plate_color
    const mainColors = ["0xC59548", "0xDDA240", "0xD9B04D", "0xD7A018", "0xEABF3E", "0xC69F29", "0xC1B76B"];
    const secondaryColors = ["0xF6CDCE", "0xD99352", "0xB7641C", "0xE5E6EA", "0xBABABA"];
    if (getRandomInt(0, 100) <= 70 && !excludeMainColors) {
        return getRandomChoice(mainColors);
    } else {
        if (getRandomInt(0, 100) <= secondaryColorsChance) {
            return getRandomChoice(secondaryColors);
        } else {
            return getRandomColor();
        }
    }
}

function getChampionshipGraphicGemsColor() {  // get_championship_graphic_gems_color
    const mainColors = ["0x001E97", "0x148A0D", "0xC51616", "0xCA0F68", "0xF4DE39", "0xBD1DAC", "0xEFEFF0"];
    if (getRandomInt(0, 100) <= 80) {
        return getRandomChoice(mainColors);
    } else {
        return getRandomColor();
    }
}

function generateCompanyName(uncommonNames = false) {  // generate_company_name
    let words = [];

    // Get number of words using weights
    let numberOfWordsWeights = [];
    for (let numberOfWords in companyNames) {
        numberOfWordsWeights.push([numberOfWords, companyNames[numberOfWords]['weight']]);
    }
    let numberOfWords = weightedChoice(numberOfWordsWeights);

    // Get common names
    let namesData = companyNames[numberOfWords];
    let firstNames = namesData['first_names']['common'];
    let middleNames = namesData['middle_names']['common'];
    let lastNames = namesData['last_names']['common'];
    // Add uncommon names
    if (uncommonNames) {
        firstNames.push(...namesData['first_names']['uncommon']);
        middleNames.push(...namesData['middle_names']['uncommon']);
        lastNames.push(...namesData['last_names']['uncommon']);
    }

    let firstWord = weightedChoice(firstNames);
    words.push(firstWord);

    // Add middle word if numberOfWords more than 2
    if (numberOfWords > 2) {
        middleNames = middleNames.filter(x => !x[0].split(' ').includes(firstWord)); // Prevent duplicate words
        let middleWord = weightedChoice(middleNames);
        words.push(...middleWord.split(' '));
    }

    lastNames = lastNames.filter(x => !words.includes(x[0]));
    let lastWord = weightedChoice(lastNames);
    words.push(lastWord);

    // Join words
    return words.join(' ');
}

async function generateEvent(existingEventNames = null) {  // generate_event
    let prestigesData = [[['C', 5], 10], [['B', 45], 8], [['A', 115], 2]];

    let eventName = generateEventName(existingEventNames);
    let newEvent = Event(eventName);
    await Events.insert(newEvent);
    let prestigeData = weightedChoice(prestigesData);
    newEvent.prestige = prestigeData[0];
    newEvent.xp = prestigeData[1];

    return newEvent;
}

function generateEventName(existingEventNames = null) {  // generate_event_name
    let singleNames = ["Uncensored", "Retribution", "Destruction", "Carnage", "Annihilation", "Sabotage", "Aggression",
        "Hatred", "Alienation", "Revulsion", "Malice", "Evolution", "Elimination", "Extinction", "Genesis",
        "Destiny", "Fallout", "Hellfire", "Heroes", "Insanity", "Believe", "Turmoil", "Pride", "Overdose",
        "Nightmare", "Anarchy", "Outbreak", "Attitude", "Warzone"];
    let prefixes = ["Deadly", "Bloody", "Epic", "Legendary", "Extreme", "Great", "Final", "Last", "Fiery", "Global",
        "Violent", "Super", "Our", "Lasting", "Dream", "Dark", "Red", "New", "Brutal", "Mega", "Dangerous"];
    let suffixes = ["Battle", "War", "Fight", "Fever", "Game", "Mania", "Slam", "Assault", "Turmoil", "Raid", "Saturday",
        "Nightmare", "Treason", "Betrayal", "Attack", "Revolution", "Sunday", "Night", "Contest",
        "Struggle", "Conquest", "Havoc", "Blast", "Thunder", "Sunday", "Night", "Day", "Showdown", "Clash"];

    let functions = [
        `${getRandomChoice(singleNames)}`,
        `${getRandomChoice(prefixes)} ${getRandomChoice(suffixes)}`
    ];

    let eventName = getRandomChoice(functions);
    // Prevent duplicates
    if (existingEventNames) {
        while (existingEventNames.includes(eventName)) {
            eventName = getRandomChoice(functions);
        }
    }

    return eventName;
}

async function generateSuperstar({popularity = null, indie = false, martian = false, name = null, gender = null,
                                  addGraphic = true, notManager = false, patreonChance = false, insert = true,
                                  generateGimmick = true} = {}) {  // generate_superstar

    // Get gender
    if (!gender) {
        gender = 'male';
        if (!martian) {
            if (getRandomInt(0, 100) <= 30) {
                if (getRandomInt(0, 100) <= 5) {
                    gender = 'non-binary'
                }
                else {
                    gender = 'female'
                }
            }
        }
    }

    // Get name
    if (name === null) {
        name = generateName(gender, null, martian)
    }

    // Get age and part timer
    let partTimer;
    let age;
    if (getRandomInt(0, 100) <= 10) {
        partTimer = true;
        age = getRandomInt(28, 40)
    }
    else {
        partTimer = false;
        age = getRandomInt(18, 32)
    }

    // Get popularity and fotc
    if (!popularity) {
        if (getRandomInt(0, 100) <= 80 || !indie) {
            popularity = getRandomInt(100, 820)
        }
        else {
            popularity = getRandomInt(820, 1030)
        }
    }
    popularity = myround(popularity, 5);
    let faceOfTheCompany = popularity >= 1000;

    // Create superstar
    const superstar = Superstar(name, popularity, age);
    if (insert) {
        await Superstars.insert(superstar);
    }
    superstar.highestRank = getRankFromPopularity(superstar.popularity);
    superstar.partTimer = partTimer;
    superstar.faceOfTheCompany = faceOfTheCompany;
    if (superstar.partTimer) {
        superstar.appearances = getPartTimerAppearances(superstar)
    }
    superstar.overness = getRandomInt(25, 75);
    superstar.alignment = getRandomChoice([null, 'face', 'heel']);
    superstar.gender = gender;
    superstar.pronoun = getRosterMemberPronoun(superstar);
    superstar.possessivePronoun = getRosterMemberPronoun(superstar, 'possessive');
    superstar.objectivePronoun = getRosterMemberPronoun(superstar, 'objective');

    // Add graphic
    if (addGraphic) {
        superstar.graphic = generateSuperstarGraphic(superstar, martian)
    }

    // Add charisma and workrate
    const xpsData = {2: 100, 1: 0};
    superstar.charisma = getRandomInt(1, 2);
    superstar.workrate = getRandomInt(1, 2);
    superstar.charismaXp = xpsData[superstar.charisma];
    superstar.workrateXp = xpsData[superstar.workrate];

    // Add manager
    if (getRandomInt(0, 100) <= 10 && !notManager) {
        superstar.fullTimeManager = true;
    }

    // Add gimmick
    if (getRandomInt(0, 100) <= 25 && !patreonChance && generateGimmick) {
        let newGimmick = Gimmick();
        await Gimmicks.insert(newGimmick);
        newGimmick.name = generateGimmickName();
        await addSuperstarGimmicks(superstar, [newGimmick]);
        superstar.debutNewGimmick = true;
    }

    // Add traits
    superstar.traits = generateSuperstarTraits();

    // Chance to add patreon superstar
    if (patreonChance && getRandomInt(0, 100) <= 25) {
        let patreonSuperstars = JSON.parse(localStorage.getItem('patreonSuperstarsData')) || [];
        if (patreonSuperstars.length) {
            let patreonSuperstar = getRandomChoice(patreonSuperstars);
            superstar.name = patreonSuperstar.name;
            if (patreonSuperstar.graphic) {
                superstar.graphic = patreonSuperstar.graphic
            }
            if (patreonSuperstar.traits) {
                superstar.traits = patreonSuperstar.traits
            }
            if (patreonSuperstar.gender) {
                superstar.gender = patreonSuperstar.gender
            }
            if (patreonSuperstar.alignment) {
                superstar.alignment = patreonSuperstar.alignment
            }
            if (patreonSuperstar.age) {
                superstar.age = patreonSuperstar.age
            }
            let newGimmick = Gimmick();
            await Gimmicks.insert(newGimmick);
            newGimmick.name = 'Patron';
            await addSuperstarGimmicks(superstar, [newGimmick]);
            superstar.debutNewGimmick = true;
        }
    }

    return superstar;
}

function generateSuperstarTraits() {
    let traits = [];
    let addTraitChance = 80;
    let allTraits = Object.keys(traitsData).filter(x => x !== 'Match Expert');

    while (getRandomInt(0, 100) <= addTraitChance && traits.length < 3) {
        let trait = getRandomChoice(allTraits);
        allTraits = allTraits.filter(x => x !== trait && !traitsData[trait]['conflicting_traits'].includes(x));
        traits.push(trait);
        addTraitChance /= 1.5;
    }

    return traits;
}

function generateSuperstarGraphic(superstar, martian = false) {  // generate_graphic
    const data = {};

    // Get parts
    let accessoryChoices = graphicsData['accessories'];
    let bottomsChoices = graphicsData['bottoms'];
    let hairChoices = graphicsData['hairs'];
    let facialHairObjectNumber = -1;
    let torsoObjectNumber = -1;
    let facialHairObject = null;
    let torsoObject = null;
    const partsSelection = superstar.gender === 'male' ? 'masculine' : superstar.gender === 'female' ? 'feminine' :
        getRandomChoice(['masculine', 'feminine']);

    // Set masculine parts
    if (partsSelection === 'masculine') {
        // Bottoms
        if (getRandomInt(0, 100) <= 95) {
            bottomsChoices = bottomsChoices.filter(x => x['style'] === 'masculine');
        } else {
            bottomsChoices = bottomsChoices.filter(x => x['style'] === 'feminine');
        }

        // Hair
        if (getRandomInt(0, 100) <= 95) {
            hairChoices = hairChoices.filter(x => x['style'] === 'masculine');
        } else {
            hairChoices = hairChoices.filter(x => x['style'] === 'feminine');
        }

        // Facial hair
        if (getRandomInt(0, 100) <= 50) {
            facialHairObject = getRandomChoice(graphicsData['facial_hairs']);
            facialHairObjectNumber = facialHairObject['number'];
        }

        // Torso
        if (getRandomInt(0, 100) <= 25) {
            let torsoChoices;
            if (getRandomInt(0, 100) <= 95) {
                torsoChoices = graphicsData['torsos'].filter(x => x['style'] === 'masculine');
            } else {
                torsoChoices = graphicsData['torsos'].filter(x => x['style'] === 'feminine');
            }
            torsoObject = getRandomChoice(torsoChoices);
            torsoObjectNumber = torsoObject['number'];
        }

        // Accessory
        if (getRandomInt(0, 100) <= 95) {
            accessoryChoices = accessoryChoices.filter(x => x['style'] === 'masculine');
        } else {
            accessoryChoices = accessoryChoices.filter(x => x['style'] === 'feminine');
        }
    }

    // Set feminine parts
    else if (partsSelection === 'feminine') {
        // Bottoms
        if (getRandomInt(0, 100) <= 10) {
            bottomsChoices = bottomsChoices.filter(x => x['style'] === 'masculine');
        } else {
            bottomsChoices = bottomsChoices.filter(x => x['style'] === 'feminine');
        }

        // Hair
        if (getRandomInt(0, 100) <= 5) {
            hairChoices = hairChoices.filter(x => x['style'] === 'masculine');
        } else {
            hairChoices = hairChoices.filter(x => x['style'] === 'feminine');
        }

        // Facial hair
        if (getRandomInt(0, 100) <= 1) {
            facialHairObject = getRandomChoice(graphicsData['facial_hairs']);
            facialHairObjectNumber = facialHairObject['number'];
        }

        // Torso
        let torsoChoices;
        if (getRandomInt(0, 100) <= 10) {
            torsoChoices = graphicsData['torsos'].filter(x => x['style'] === 'masculine');
        } else {
            torsoChoices = graphicsData['torsos'].filter(x => x['style'] === 'feminine');
        }
        torsoObject = getRandomChoice(torsoChoices);
        torsoObjectNumber = torsoObject['number'];

        // Accessory
        if (getRandomInt(0, 100) <= 20) {
            accessoryChoices = accessoryChoices.filter(x => x['style'] === 'masculine');
        } else {
            accessoryChoices = accessoryChoices.filter(x => x['style'] === 'feminine');
        }
    }

    // REQUIRED PARTS

    // Body
    let bodyObject;
    if (partsSelection === 'masculine') {
        bodyObject = 0;
    } else if (partsSelection === 'feminine') {
        bodyObject = 3;
    } else {
        bodyObject = 0;
    }

    // Body color
    let bodyColor;
    if (!martian) {
        bodyColor = getRandomInt(0, 100) <= 99 ? getRandomChoice(bodyColorsList) : getRandomColor();
    } else {
        bodyColor = '0xD2FFAF';
    }
    data['body'] = {
        'object': bodyObject,
        'colors': [bodyColor]
    };

    // Eyes
    const eyesObject = getRandomChoice(graphicsData['eyes']);
    const eyesObjectNumber = eyesObject['number'];

    // Eyes color
    const eyesColors = [];
    for (const x of Array(eyesObject['designs']).keys()) {
        const eyesColor = getRandomInt(0, 100) <= 95 ? '0x000000' : getRandomColor();
        eyesColors.push(eyesColor);
    }

    data['eyes'] = {
        'object': eyesObjectNumber,
        'colors': eyesColors
    };

    // Shoes
    const shoesObject = getRandomChoice(graphicsData['shoes']);
    const shoesObjectNumber = shoesObject['number'];

    // Shoes color
    const shoesColors = [];
    let shoeColor = null;
    for (const x of Array(shoesObject['designs']).keys()) {
        if (shoeColor) {
            if (getRandomInt(0, 100) <= 50) {
                shoeColor = getRandomInt(0, 100) <= 70 ? shoeColor = getSimilarColor(shoeColor) : getRandomColor();
            }
        } else {
            shoeColor = getRandomInt(0, 100) <= 30 ? '0x1E1E1E' : getRandomColor();
        }
        shoesColors.push(shoeColor);
    }

    data['shoes'] = {
        'object': shoesObjectNumber,
        'colors': shoesColors
    };

    // Bottoms
    const bottomsObject = getRandomChoice(bottomsChoices);
    const bottomsObjectNumber = bottomsObject['number'];

    // Bottoms color
    const bottomsColors = [];
    let bottomsColor = null;
    for (const x of Array(bottomsObject['designs']).keys()) {
        if (bottomsColor) {
            if (getRandomInt(0, 100) <= 75) {
                bottomsColor = getRandomInt(0, 100) <= 50 ? bottomsColor = getSimilarColor(bottomsColor) : getRandomColor();
            }
        } else {
            bottomsColor = getRandomColor();
        }
        bottomsColors.push(bottomsColor);
    }

    data['bottoms'] = {
        'object': bottomsObjectNumber,
        'colors': bottomsColors
    };

    // Hair
    const hairObject = getRandomChoice(hairChoices);
    const hairObjectNumber = hairObject['number'];

    // Hair color
    const hairColors = [];
    let hairColor = null;
    for (const x of Array(hairObject['designs']).keys()) {
        if (hairColor) {
            if (getRandomInt(0, 100) <= 20) {
                hairColor = getHairColor();
            }
        } else {
            hairColor = getHairColor();
        }
        hairColors.push(hairColor);
    }

    data['hair'] = {
        'object': hairObjectNumber,
        'colors': hairColors
    };

    // OPTIONAL PARTS

    // Elbow
    let elbowObjectNumber = -1;
    let elbowObject = null;
    if (getRandomInt(0, 100) <= 20) {
        elbowObject = getRandomChoice(graphicsData['elbows']);
        elbowObjectNumber = elbowObject['number'];
    }

    // Elbow color
    const elbowColors = [];
    if (elbowObjectNumber >= 0) {
        for (const x of Array(elbowObject['designs']).keys()) {
            let elbowColor;
            if (x > 0) {
                elbowColor = getRandomColor();
            } else {
                elbowColor = shoeColor;
            }
            elbowColors.push(elbowColor);
        }
    }

    data['elbow'] = {
        'object': elbowObjectNumber,
        'colors': elbowColors
    };

    // Kneepad
    let kneeObjectNumber = -1;
    let kneeObject = null;
    if (getRandomInt(0, 100) <= 20) {
        kneeObject = getRandomChoice(graphicsData['kneepads']);
        kneeObjectNumber = kneeObject['number'];
    }

    // Kneepad color
    const kneeColors = [];
    if (kneeObjectNumber >= 0) {
        for (const x of Array(kneeObject['designs']).keys()) {
            let kneeColor;
            if (x > 0) {
                kneeColor = getRandomColor();
            } else {
                kneeColor = shoeColor;
            }
            kneeColors.push(kneeColor);
        }
    }

    data['knee'] = {
        'object': kneeObjectNumber,
        'colors': kneeColors
    };

    // Facepaint
    let facepaintObjectNumber = -1;
    let facepaintObject = null;
    if (getRandomInt(0, 100) <= 5) {
        facepaintObject = getRandomChoice(graphicsData['facepaints']);
        facepaintObjectNumber = facepaintObject['number'];
    }

    // Facepaint color
    const facepaintColors = [];
    if (facepaintObjectNumber >= 0) {
        for (const x of Array(facepaintObject['designs']).keys()) {
            const facepaintColor = getRandomColor();
            facepaintColors.push(facepaintColor);
        }
    }

    data['facepaint'] = {
        'object': facepaintObjectNumber,
        'colors': facepaintColors
    };

    // Tattoos
    let tattooObjectNumber = -1;
    let tattooObject = null;
    if (getRandomInt(0, 100) <= 10) {
        tattooObject = getRandomChoice(graphicsData['tattoos']);
        tattooObjectNumber = tattooObject['number'];
    }

    // Tattoo color
    const tattooColors = [];
    if (tattooObjectNumber >= 0) {
        for (const x of Array(tattooObject['designs']).keys()) {
            const tattooColor = getRandomInt(0, 100) <= 95 ? '0x555555' : getRandomColor();
            tattooColors.push(tattooColor);
        }
    }

    data['tattoo'] = {
        'object': tattooObjectNumber,
        'colors': tattooColors
    };

    // Facial hair color
    const facialHairColors = [];
    let facialHairColor = hairColor;
    if (facialHairObjectNumber >= 0) {
        for (const x of Array(facialHairObject['designs']).keys()) {
            facialHairColors.push(facialHairColor);
        }
    }

    data['facialHair'] = {
        'object': facialHairObjectNumber,
        'colors': facialHairColors
    };

    // Torso color
    const torsoColors = [];
    let torsoColor = null;
    if (torsoObjectNumber >= 0) {
        for (const x of Array(torsoObject['designs']).keys()) {
            if (torsoColor) {
                if (getRandomInt(0, 100) <= 75) {
                    torsoColor = getRandomInt(0, 100) <= 50 ? torsoColor = getSimilarColor(torsoColor) : getRandomColor();
                }
            } else {
                torsoColor = getRandomColor();
            }
            torsoColors.push(torsoColor);
        }
    }

    data['torso'] = {
        'object': torsoObjectNumber,
        'colors': torsoColors
    };

    // Accessories
    let accessoryObject;
    let accessoryObjectNumber = -1;
    if (getRandomInt(0, 100) <= 5) {
        accessoryObject = getRandomChoice(accessoryChoices);
        accessoryObjectNumber = accessoryObject['number'];
    }
    const accessoryColors = [];
    if (accessoryObjectNumber >= 0) {
        let accessoryColor = null;
        for (const x of Array(accessoryObject['designs']).keys()) {
            if (accessoryColor) {
                if (getRandomInt(0, 100) <= 50) {
                    accessoryColor = getRandomInt(0, 100) <= 75 ? accessoryColor = getSimilarColor(accessoryColor) : getRandomColor();
                }
            }
            else {
                accessoryColor = getRandomColor();
            }
            accessoryColors.push(accessoryColor);
        }
    }

    data['accessory'] = {
        'object': accessoryObjectNumber,
        'colors': accessoryColors
    };

    let accessory2Object;
    let accessory2ObjectNumber = -1;
    if (getRandomInt(0, 100) <= 5) {
        accessory2Object = getRandomChoice(accessoryChoices);
        accessory2ObjectNumber = accessory2Object['number'];
    }
    const accessory2Colors = [];
    if (accessory2ObjectNumber >= 0) {
        let accessory2Color = null;
        for (const x of Array(accessory2Object['designs']).keys()) {
            if (accessory2Color) {
                if (getRandomInt(0, 100) <= 50) {
                    accessory2Color = getRandomInt(0, 100) <= 75 ? accessory2Color = getSimilarColor(accessory2Color) : getRandomColor();
                }
            }
            else {
                accessory2Color = getRandomColor();
            }
            accessory2Colors.push(accessory2Color);
        }
    }

    data['accessory2'] = {
        'object': accessory2ObjectNumber,
        'colors': accessory2Colors
    };

    let accessory3Object;
    let accessory3ObjectNumber = -1;
    if (getRandomInt(0, 100) <= 5) {
        accessory3Object = getRandomChoice(accessoryChoices);
        accessory3ObjectNumber = accessory3Object['number'];
    }
    const accessory3Colors = [];
    if (accessory3ObjectNumber >= 0) {
        let accessory3Color = null;
        for (const x of Array(accessory3Object['designs']).keys()) {
            if (accessory3Color) {
                if (getRandomInt(0, 100) <= 50) {
                    accessory3Color = getRandomInt(0, 100) <= 75 ? accessory3Color = getSimilarColor(accessory3Color) : getRandomColor();
                }
            }
            else {
                accessory3Color = getRandomColor();
            }
            accessory3Colors.push(accessory3Color);
        }
    }

    data['accessory3'] = {
        'object': accessory3ObjectNumber,
        'colors': accessory3Colors
    };

    return data;
}

async function generateTagTeam(superstars) { // generate_tagteam
    const names = {
        prefix: ['The', 'Big', 'Dangerous', 'American', 'Universal', 'The', 'Wasted', 'Natural', 'Stiff',
                   'Hard', 'Beat Down', 'Fabulous', 'The', 'Wild', 'The', 'Rebel', 'Hope', 'Hate', 'Hell',
                   'Undisputed', 'Top', 'Dirty', 'Fancy', 'Ultimate', 'Pretty', 'The', 'Lion', 'Torn', 'Deadly',
                   'Danger', 'Ultimate', 'Undisputed', 'Gorgeous', 'Glorious', 'Odd', 'Thunder', 'Raw'],
        suffixes: ['Killers', 'Butchers', 'Legends', 'Guys', 'Destroyers', 'Busters', 'Team', 'Crew', 'Brothers',
                  'Express', 'Foundation', 'Beta', 'Damnation', 'Grapplers', 'Fighters', 'Survivors',
                  'Emergence', 'Future', 'Disruptors', 'Warriors', 'Duo', 'Squad', 'Bodies', 'Twins', 'Bunch',
                  'Boys', 'Fighters', 'Stars', 'Boyz', 'Dream', 'Gang', 'Club', 'Squad']
    };

    let firstSuperstarName = superstars[0].name;
    let secondSuperstarName = superstars[1].name;
    const s1NameSplit = firstSuperstarName.split(' ');
    const s2NameSplit = secondSuperstarName.split(' ');

    const choices = [
        `${s1NameSplit[s1NameSplit.length - 1]} & ${s2NameSplit[s2NameSplit.length - 1]}`,
        `${s1NameSplit[s1NameSplit.length - 1]} and ${s2NameSplit[s2NameSplit.length - 1]}`,
        `${getRandomChoice(names.prefix)} ${getRandomChoice(names.suffixes)}`
    ];
    // Add joint names if long enough
    if (s1NameSplit[s1NameSplit.length - 1].length > 4 && s2NameSplit[s2NameSplit.length - 1].length > 4) {
        choices.push(`${s1NameSplit[s1NameSplit.length - 1].substring(0, getRandomInt(2, s1NameSplit[s1NameSplit.length - 1].length))}` +
            `${s2NameSplit[s2NameSplit.length - 1].substring(0, getRandomInt(2, s2NameSplit[s2NameSplit.length - 1].length))}`);
    }
    // Add family names
    let name;
    if (s1NameSplit[s1NameSplit.length - 1] === s2NameSplit[s2NameSplit.length - 1]) {
        name = `${s1NameSplit[s1NameSplit.length - 1]} ${getRandomChoice(names.suffixes)}`;
    }
    else {
        name = getRandomChoice(choices);
    }

    const newTagTeam = TagTeam(name);
    await TagTeams.insert(newTagTeam);
    await addTagTeamSuperstars(newTagTeam, superstars);

    return newTagTeam;
}

async function generateTrio(superstars) {  // generate_trio
    const names = {
        prefix: ['The', 'Huge', 'Hot', 'Global', 'The', 'Angry', 'Natural', 'Stiff', 'Disaster',
                   'Beat Down', 'Pyro', 'The', 'Wild', 'The', 'Rebel', 'Hope', 'Hate', 'Heaven',
                   'Undisputed', 'Top', 'Pretty', 'The', 'Tiger', 'Torn', 'Deadly', 'Humongous', 'The',
                   'Danger', 'Ultimate', 'Cool', 'Odd', 'Snow', 'Rainbow', 'Thunder', 'Hungry', 'Fierce',
                   'Unholy', 'Force', 'Headlock', 'Dropkick', 'Power', 'New', 'X', 'Full', 'The', 'Mean'],
        suffixes: ['Trinity', 'Team', 'Opposition', 'Alliance', 'Pact', 'Group', 'Era', 'People', 'Three', 'Trio',
                  'Mafia', '3', 'Treble', 'Triple', 'Triad', 'Band', 'Club', 'Faction', 'Gang', 'Party', 'Clique',
                  'Crew', 'Posse', 'Fellas', 'Squad', 'Evolution', 'Wrestlers', 'Coalition', 'Style', 'Animals',
                  'Threat', 'World Order', 'Dragons', 'Family', 'Generation']
    };

    const name = `${getRandomChoice(names.prefix)} ${getRandomChoice(names.suffixes)}`;

    const newTagTeam = TagTeam(name);
    await TagTeams.insert(newTagTeam);
    newTagTeam.trio = true;
    await addTagTeamSuperstars(newTagTeam, superstars);

    return newTagTeam;
}

async function generateFaction(factionSuperstars, rosterFactions, rosterTeams) {  // generate_faction
    // Get leader superstar
    let leaderSuperstar = null;
    if (getRandomInt(0, 100) <= 60) {
        leaderSuperstar = getRandomChoice(factionSuperstars);
    }

    // Get faction name
    let unavailableFactionNames = [...rosterFactions, ...rosterTeams].map(x => x.name);
    let factionName = getRandomFactionName();
    while (unavailableFactionNames.includes(factionName)) {
        factionName = getRandomFactionName();
    }

    // Create faction
    const {reputation, xp} = getFactionCreationReputationAndXp(factionSuperstars, leaderSuperstar);
    return createFaction(factionName, factionSuperstars, reputation, xp, leaderSuperstar);
}

function getRandomFactionName() {  // get_random_faction_name
    let names = {
        'prefix': ['The', 'Big', 'Dangerous', 'American', 'Universal', 'The', 'Natural', 'Fantastic', 'Marvelous',
                   'Fabulous', 'The', 'Wild', 'The', 'Rebel', 'Hell', 'Undisputed', 'Top', 'Fancy', 'Drug',
                   'Ultimate', 'Pretty', 'The', 'Deadly', 'Ultimate', 'Undisputed', 'Gorgeous', 'Glorious', 'Thunder',
                   'Original', 'Elite', 'Premier', 'Perfect', 'Street', 'Executive', 'Cool', 'Main Event'],
        'names': ['Killers', 'Legends', 'Guys', 'Crew', 'Express', 'Foundation', 'Fighters', 'Squad',
                  'Stars', 'Gang', 'Club', 'Squad', 'Alliance', 'Family', 'Posse', 'Order', 'Society', 'Association',
                  'Circle', 'Corporation', 'Group', 'League', 'Clique', 'Clan', 'Gang', 'Syndicate', 'Band', 'Mafia']
    };
    return `${getRandomChoice(names['prefix'])} ${getRandomChoice(names['names'])}`;
}

async function generateStaffMember(role, level = null) {  // Previously generate_staff_member
    const staffMember = Staff();
    staffMember.role = role;

    // Generate gender
    if (getRandomInt(0, 100) <= 50) {
        staffMember.gender = 'male'
    }
    else {
        if (getRandomInt(0, 100) <= 75) {
            staffMember.gender = 'female'
        }
        else {
            staffMember.gender = 'non-binary'
        }
    }

    // Generate name, age, graphic
    staffMember.name = generateStaffName(staffMember.role, staffMember.gender);
    staffMember.age = getRandomInt(20, 60);
    staffMember.graphic = generateStaffGraphic(staffMember.gender, staffMember.role);

    // Add level, affected by age
    if (level === null) {
        const levelWeights = staffMember.age <= 45 ? [[1, 35], [2, 35], [3, 30]] : [[1, 10], [2, 45], [3, 45]];
        level = weightedChoice(levelWeights);
    }
    staffMember.level = level;

    // Add xp
    const xps = {1: 0, 2: 100, 3: 300};
    const xpDeviations = [0, 0, 0, 0, 2, 4, 5, 5, 6, 7, 8, 8, 9, 10, 10];
    staffMember.xp = (xps[staffMember.level] || 0) + getRandomChoice(xpDeviations);

    // Save
    StaffMembers.insert(staffMember);

    return staffMember;
}

function generateStaffName(role = null, gender = 'male', martian = false) {  // Previously generate_staff_name
    let name;
    let firstNameOptions;
    let firstName;

    if (martian) {
        name = `${getRandomChoice(staffMartianFirstNames)} ${staffMartianLastNames}`
    }
    else {
        if (role === 'doctor') {
            firstName = 'Dr.'
        }
        else {
            if (gender === 'non-binary') {
                firstNameOptions = staffFirstNames['male'].concat(staffLastNames['female'])
            }
            else {
                firstNameOptions = staffFirstNames[gender]
            }
            firstName = getRandomChoice(firstNameOptions)
        }

        let lastName = getRandomChoice(staffLastNames);

        if (firstName && lastName) {
            name = `${firstName} ${lastName}`
        }
        else if (firstName) {
            name = firstName
        }
        else if (lastName) {
            name = lastName
        }
        else {
            name = 'Staff'
        }
    }

    return name;
}

function generateStaffGraphic(gender, role = null, martian = false) {  // generate_staff_graphic
    const data = {};
    let hairChoices = graphicsData['hairs'];
    let facialHairObjectNumber = -1;
    let facialHairObject = null;

    // Get staff parts
    let shoes = graphicsData['shoes'].filter(x => x['staff']);
    let torsos = graphicsData['torsos'].filter(x => x['staff']);
    let bottoms = graphicsData['bottoms'].filter(x => x['staff']);
    let accessories = graphicsData['accessories'].filter(x => x['staff']);
    if (role === 'doctor') {
        accessories = accessories.filter(x => x['category'] === 'doctor');
    } else {
        accessories = accessories.filter(x => x['category'] !== 'doctor');
    }
    const partsSelection = gender === 'male' ? 'masculine' : gender === 'female' ? 'feminine' :
        getRandomChoice(['masculine', 'feminine']);

    // Set masculine parts
    if (partsSelection === 'masculine') {
        // Hair
        hairChoices = graphicsData['hairs'].filter(x => x['style'] === 'masculine');

        // Facial hair
        if (getRandomInt(0, 100) <= 50) {
            facialHairObject = getRandomChoice(graphicsData['facial_hairs']);
            facialHairObjectNumber = facialHairObject['number'];
        }

        // Torsos
        torsos = torsos.filter(x => x['style'] !== 'feminine');

        // Bottoms
        bottoms = bottoms.filter(x => x['style'] !== 'feminine');
    }
    // Set feminine parts
    else if (partsSelection === 'feminine') {
        hairChoices = graphicsData['hairs'].filter(x => x['style'] === 'feminine');

        // Bottoms
        bottoms = bottoms.filter(x => x['style'] !== 'masculine');
    }

    // REQUIRED PARTS

    // Body
    let bodyObject;
    if (partsSelection === 'masculine') {
        bodyObject = 0;
    } else if (partsSelection === 'feminine') {
        bodyObject = 3;
    } else {
        bodyObject = 0;
    }

    // Body color
    let bodyColor;
    if (!martian) {
        bodyColor = getRandomChoice(bodyColorsList);
    } else {
        bodyColor = '0xD2FFAF';
    }
    data['body'] = {
        'object': bodyObject,
        'colors': [bodyColor]
    };

    // Eyes
    const eyesObject = getRandomChoice(graphicsData['eyes']);
    const eyesObjectNumber = eyesObject['number'];

    // Eyes color
    const eyesColors = [];
    for (const x of Array(eyesObject['designs']).keys()) {
        const eyesColor = '0x000000';
        eyesColors.push(eyesColor);
    }

    data['eyes'] = {
        'object': eyesObjectNumber,
        'colors': eyesColors
    };

    // Hair
    const hairObject = getRandomChoice(hairChoices);
    const hairObjectNumber = hairObject['number'];

    // Hair color
    const hairColors = [];
    let hairColor = null;
    for (const x of Array(hairObject['designs']).keys()) {
        if (!hairColor) {
            hairColor = getRandomChoice(hairColorsList);
        }
        hairColors.push(hairColor);
    }
    data['hair'] = {
        'object': hairObjectNumber,
        'colors': hairColors
    };

    // Shoes
    const shoesObject = getRandomChoice(shoes);
    const shoesObjectNumber = shoesObject['number'];

    // Shoes color
    const shoesColors = [];
    let shoeColor = null;
    for (const x of Array(shoesObject['designs']).keys()) {
        if (shoeColor) {
            if (getRandomInt(0, 100) <= 10) {
                if (shoeColor === '0x1E1E1E' && getRandomInt(0, 100) <= 80) {
                    shoeColor = getSimilarColor(shoeColor, true);
                } else {
                    shoeColor = getSimilarColor(shoeColor);
                }
            }
        } else {
            shoeColor = getRandomInt(0, 100) <= 95 ? getRandomChoice(shoesColorsList) : getRandomColor();
        }
        shoesColors.push(shoeColor);
    }

    data['shoes'] = {
        'object': shoesObjectNumber,
        'colors': shoesColors
    };

    // Bottoms
    const bottomsObject = getRandomChoice(bottoms);
    const bottomsObjectNumber = bottomsObject['number'];

    // Bottoms color
    const bottomsColors = [];
    let bottomsColor = null;
    for (const x of Array(bottomsObject['designs']).keys()) {
        if (bottomsColor) {
            bottomsColor = getSimilarColor(bottomsColor);
        } else {
            bottomsColor = getRandomInt(0, 100) <= 95 ? getRandomChoice(pantsColorsList) : getRandomColor();
        }
        bottomsColors.push(bottomsColor);
    }

    data['bottoms'] = {
        'object': bottomsObjectNumber,
        'colors': bottomsColors
    };

    // Torso
    let torsoObject = getRandomChoice(torsos);
    let torsoObjectNumber = torsoObject['number'];

    // Torso color
    const torsoColors = [];
    if (torsoObjectNumber >= 0) {
        let torsoColor = null;
        for (const x of Array(torsoObject['designs']).keys()) {
            if (torsoColor) {
                if (getRandomInt(0, 100) <= 30) {
                    torsoColor = getSimilarColor(torsoColor, false, 'light');
                }
            } else {
                torsoColor = getRandomInt(0, 100) <= 95 ? getRandomChoice(shirtsColorsList) : getRandomColor()
            }
            torsoColors.push(torsoColor);
        }
    }

    data['torso'] = {
        'object': torsoObjectNumber,
        'colors': torsoColors
    };

    // OPTIONAL PARTS

    // Facial hair
    const facialHairColors = [];
    let facialHairColor = hairColor;
    if (facialHairObjectNumber >= 0) {
        for (const x of Array(facialHairObject['designs']).keys()) {
            facialHairColors.push(facialHairColor);
        }
    }

    data['facialHair'] = {
        'object': facialHairObjectNumber,
        'colors': facialHairColors
    };

    // Accessories
    let accessoryObjectNumber = -1;
    let accessoryObject = null;
    if (getRandomInt(0, 100) <= 50 || role === 'doctor') {
        accessoryObject = getRandomChoice(accessories);
        accessoryObjectNumber = accessoryObject['number'];
    }
    const accessoryColors = [];
    if (accessoryObjectNumber >= 0) {
        let accessoryColor = null;

        // Get colors list for specific items
        let colorsList = [];
        if (accessoryObject['category'] === 'suit') {
            colorsList = jacketsColorsList;
        } else if (accessoryObject['category'] === 'glasses') {
            colorsList = glassesColorsList;
        } else if (accessoryObject['category'] === 'doctor') {
            colorsList = doctorCoatColorsList;
        }

        for (const x of Array(accessoryObject['designs']).keys()) {
            if (accessoryColor) {
                if (getRandomInt(0, 100) <= 30) {
                    accessoryColor = getSimilarColor(accessoryColor, false, 'light');
                }
            } else {
                if (colorsList.length > 0) {
                    accessoryColor = getRandomInt(0, 100) <= 95 ? getRandomChoice(colorsList) : getRandomColor();
                } else {
                    accessoryColor = getRandomColor();
                }
            }
            accessoryColors.push(accessoryColor);
        }
    }

    data['accessory'] = {
        'object': accessoryObjectNumber,
        'colors': accessoryColors
    };

    // EMPTY PARTS
    data['elbow'] = {'object': -1, 'colors': []};
    data['knee'] = {'object': -1, 'colors': []};
    data['facepaint'] = {'object': -1, 'colors': []};
    data['tattoo'] = {'object': -1, 'colors': []};
    data['accessory2'] = {'object': -1, 'colors': []};
    data['accessory3'] = {'object': -1, 'colors': []};

    return data;
}

function getSimilarColor(color, monochrome = false, luminosity = null) {
    let colorHue;
    if (monochrome) {
        colorHue = 'monochrome';
    } else {
        colorHue = '#' + color.substring(2);
    }
    const selectedColor = randomColor({
        hue: colorHue,
        luminosity
    });
    return '0x' + selectedColor.substring(1);
}

function generateGimmickName() {
    const gimmickNames = ['Legend Killer', 'Grim Reaper', 'Rock Star', 'Crime Boss', 'General Manager', 'Demon Hunter',
        'Secret Service', 'Career Killer', 'Unleashed Animal', 'Wrestling God', 'Parole Officer',
        'Crazy Russian', 'Country Singer', 'Outdoorsman', 'Little Dog', 'Bulldog', 'Brawler',
        'Romantic', 'Hugger', 'Lovesick', 'Royalty', 'Comic-book Hero', 'Comic-book Villain',
        'Supermodel', 'Hacker', 'Redneck', 'Bodyguard', 'Rebel', 'Monk', 'Hypocrite',
        'Hustler', 'Butcher', 'Surfer', 'Skater', 'Ninja', 'Iceman', 'Dancer',
        'Poser', 'Hoser', 'Lumberjack'];
    return getRandomChoice(gimmickNames);
}

export {generateCompany, generateSuperstar, generateTagTeam, generateTrio, generateChampionshipName,
    getChampionshipGraphicStrapColor, getChampionshipGraphicGemsColor, getChampionshipGraphicPlateColor,
    generateStaffMember, generateStaffName, generateStaffGraphic, generateSuperstarGraphic, generateFaction,
    generateChampionshipGraphic, generateGimmickName, filterCompetitorPowerups, generateCustomStartingCompany}